import React, { useState } from 'react';
import { ClusterDto, OneWayInFeedbackUpsertDto, OneWayInOpportunityDto } from 'src/api/v2';
import { Counties } from 'src/features/one-way-in/constants/counties';
import useOneWayInFeedback from 'src/features/one-way-in/hooks/use-one-way-in-feedback';
import AdditionalFeedback from './AdditionalFeedback';
import FeedBackSent from './FeedBackSent';
import InitialFeedback from './InitialFeedback';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@innovationnorway/cl-react';
import ReferModal from '../referral/ReferModal';

type Props = {
  formId: string | undefined;
  opportunity: OneWayInOpportunityDto | undefined;
  cluster: ClusterDto | undefined;
  onClose?: () => void;
  onFeedbackFinished?: () => void;
};

enum FeedbackFlowState {
  Initial = 'initial',
  Additional = 'additional',
  Sent = 'sent',
  Refer = 'refer',
  Finished = 'finished',
}

const OpportunityFeedback: React.FC<Props> = ({
  formId,
  opportunity,
  cluster,
  onClose,
  onFeedbackFinished,
}) => {
  const { t: tOneWayIn } = useTranslation('oneWayIn');
  const [flowState, setFlowState] = useState<FeedbackFlowState>(FeedbackFlowState.Initial);

  const { opportunityFeedbackCollection: existingFeedback, upsertWithoutSideEffects: upsertMutation } =
    useOneWayInFeedback(formId);

  const submitOpportunityFeedback = (correctlyReferred: boolean, comment?: string) => {
    if (!cluster?.id || !opportunity?.id) return;

    const record = {
      id: existingFeedback?.actorFeedback?.id || undefined,
      clusterId: cluster.id,
      oneWayInOpportunityD365Id: formId,
      title: existingFeedback?.actorFeedback?.title || opportunity.title,
      municipality: existingFeedback?.actorFeedback?.municipality || opportunity.municipality,
      county:
        existingFeedback?.actorFeedback?.county ||
        Counties.find((county) =>
          county.municipalities.some((municipality) => municipality === opportunity.municipality),
        )?.countyName ||
        undefined,
      comment: comment || existingFeedback?.actorFeedback?.comment,
      wasCorrectlyReferred: correctlyReferred,
      suggestedActors: existingFeedback?.actorFeedback?.suggestedActors || [],
    } as OneWayInFeedbackUpsertDto;

    upsertMutation.mutate(record, {
      onSuccess: () => {
        if (correctlyReferred) {
          onFeedbackFinished?.();
          toast.success(tOneWayIn('opportunity.feedback.answerSent'));
          setFlowState(FeedbackFlowState.Finished);
        } else {
          setFlowState(FeedbackFlowState.Sent);
        }
      },
    });
  };

  const onInitialFeedbackProvided = (isCorrectlyReferred: boolean) => {
    if (isCorrectlyReferred) {
      submitOpportunityFeedback(true);
    } else {
      setFlowState(FeedbackFlowState.Additional);
    }
  };

  const handleDialogClose = (expectedState: FeedbackFlowState, nextState: FeedbackFlowState) => {
    // Ignore close events triggered by state transitions when another step is now active.
    if (flowState !== expectedState) return;

    onClose?.();
    setFlowState(nextState);
  };

  return (
    <>
      {(flowState === FeedbackFlowState.Initial || flowState === FeedbackFlowState.Additional) && (
        <InitialFeedback clusterName={cluster?.name} onInitialFeedbackProvided={onInitialFeedbackProvided} />
      )}
      <Dialog
        open={flowState === FeedbackFlowState.Additional}
        onClose={() => handleDialogClose(FeedbackFlowState.Additional, FeedbackFlowState.Initial)}
        closedby="any"
      >
        <AdditionalFeedback
          onSubmit={(comment) => submitOpportunityFeedback(false, comment)}
          onClose={() => handleDialogClose(FeedbackFlowState.Additional, FeedbackFlowState.Initial)}
        />
      </Dialog>
      <Dialog
        open={flowState === FeedbackFlowState.Sent}
        onClose={() => handleDialogClose(FeedbackFlowState.Sent, FeedbackFlowState.Finished)}
        closedby="any"
      >
        <FeedBackSent
          onSubmit={() => setFlowState(FeedbackFlowState.Refer)}
          onClose={() => handleDialogClose(FeedbackFlowState.Sent, FeedbackFlowState.Finished)}
        />
      </Dialog>
      <ReferModal
        dialogOpen={flowState === FeedbackFlowState.Refer}
        onClose={() => {
          handleDialogClose(FeedbackFlowState.Refer, FeedbackFlowState.Finished);
          onFeedbackFinished?.();
        }}
      />
    </>
  );
};

export default OpportunityFeedback;
